<script setup lang="ts">
// Rich tool viewparts for an assistant turn - the SINGLE place (Studio, the
// quick-ask popover, the Home thread, and the extension panel all use it) that
// turns structured tool results into in-conversation UI. Iterates the bubble's
// tools and, for any tool with a registered renderer (lib/toolRenderers.ts),
// renders it passing the ToolStep. Tools without a renderer show nothing here -
// the ToolFold audit row still covers them uniformly.
import { computed } from 'vue';
import type { ToolStep } from '../types';
import { TOOL_RENDERERS } from '../lib/toolRenderers';
import { citationOffsets } from '../lib/citations';

const props = defineProps<{ tools: ToolStep[] }>();

// Flattened citation index where each tool's rows start, so a viewpart can tag
// each row with the [n] a CitationChip scrolls to (see lib/citations.ts).
const offsets = computed(() => citationOffsets(props.tools));

// The same tool arrives under two names: bare (`search_web`) on the fast lane,
// and SDK-prefixed (`mcp__hq__search_web`) on the agent/MCP path. The prefix is
// `mcp__<server>__`; `__` is the separator (tool names keep single
// underscores). Strip it so the registry is keyed by the bare tool name once.
function baseName(name: string): string {
  return name.startsWith('mcp__') ? name.split('__').slice(2).join('__') : name;
}
function rendererFor(name: string) {
  return TOOL_RENDERERS[baseName(name)];
}
</script>

<template>
  <template v-for="(tool, i) in tools" :key="i">
    <component
      v-if="rendererFor(tool.name)"
      :is="rendererFor(tool.name)"
      :tool="tool"
      :base="offsets[i] ?? 0"
    />
  </template>
</template>
